// PillStack design tokens — single source of truth for colors, type, spacing, radii, shadows.
// Import from here instead of hardcoding values in components.

export const colors = {
  brand: {
    50: "#E8F9F7",
    100: "#CFF3EE",
    200: "#9FE6DD",
    300: "#63D4C4",
    400: "#2FB9A8",
    500: "#0D9E8C",
    600: "#0B8677",
    700: "#086E62",
    800: "#065F52",
    900: "#044740",
  },
  accent: {
    50: "#FFF1EB",
    100: "#FFDCCB",
    200: "#FFB999",
    300: "#FF9666",
    400: "#FF7B40",
    500: "#FF6B35",
    600: "#E8541F",
    700: "#C24217",
    800: "#8E2F0F",
  },
  warning: {
    50: "#FEF3C7",
    100: "#FDE68A",
    500: "#F59E0B",
    600: "#D97706",
    700: "#B45309",
  },
  success: {
    50: "#D1FAE5",
    100: "#A7F3D0",
    500: "#10B981",
    600: "#059669",
    700: "#047857",
  },
  error: {
    50: "#FEE2E2",
    100: "#FECACA",
    500: "#EF4444",
    600: "#DC2626",
    700: "#B91C1C",
  },
  info: {
    50: "#DBEAFE",
    100: "#BFDBFE",
    500: "#3B82F6",
    600: "#2563EB",
    700: "#1D4ED8",
  },
  neutral: {
    0: "#FFFFFF",
    50: "#F8FAFC",
    100: "#F1F5F9",
    200: "#E2E8F0",
    300: "#CBD5E1",
    400: "#94A3B8",
    500: "#64748B",
    600: "#475569",
    700: "#334155",
    800: "#1E293B",
    900: "#0F172A",
  },
};

export const gradients = {
  brand: `linear-gradient(135deg, ${colors.brand[500]} 0%, ${colors.brand[600]} 100%)`,
  brandSoft: `linear-gradient(135deg, ${colors.brand[50]} 0%, ${colors.brand[100]} 100%)`,
  brandBold: `linear-gradient(135deg, ${colors.brand[400]} 0%, ${colors.brand[700]} 100%)`,
  heroBg: `linear-gradient(180deg, ${colors.neutral[0]} 0%, ${colors.brand[50]} 100%)`,
  accent: `linear-gradient(135deg, ${colors.accent[400]} 0%, ${colors.accent[600]} 100%)`,
  success: `linear-gradient(135deg, ${colors.success[500]} 0%, ${colors.success[700]} 100%)`,
  warning: `linear-gradient(135deg, ${colors.warning[500]} 0%, ${colors.warning[700]} 100%)`,
};

// Typography scale — Inter as primary, Plus Jakarta Sans as fallback
export const fontFamily = `'Inter', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`;

export const type = {
  display: { size: "48px", weight: 800, lineHeight: "1.05", letterSpacing: "-0.04em" },
  h1: { size: "36px", weight: 800, lineHeight: "1.12", letterSpacing: "-0.03em" },
  h2: { size: "28px", weight: 700, lineHeight: "1.2", letterSpacing: "-0.02em" },
  h3: { size: "20px", weight: 700, lineHeight: "1.3", letterSpacing: "-0.01em" },
  body: { size: "15px", weight: 400, lineHeight: "1.6" },
  bodyStrong: { size: "15px", weight: 600, lineHeight: "1.6" },
  caption: { size: "13px", weight: 500, lineHeight: "1.4" },
  micro: { size: "11px", weight: 600, lineHeight: "1.3", letterSpacing: "0.08em", textTransform: "uppercase" },
};

// 8pt grid
export const space = {
  0: "0",
  1: "4px",
  2: "8px",
  3: "12px",
  4: "16px",
  5: "24px",
  6: "32px",
  7: "48px",
  8: "64px",
  9: "96px",
};

export const radii = {
  xs: "6px",
  sm: "10px",
  md: "14px",
  lg: "18px",
  xl: "24px",
  "2xl": "32px",
  full: "9999px",
};

export const shadows = {
  xs: "0 1px 2px rgba(15,23,42,0.04)",
  sm: "0 2px 8px rgba(15,23,42,0.06)",
  md: "0 6px 20px rgba(15,23,42,0.08)",
  lg: "0 14px 36px rgba(15,23,42,0.10)",
  xl: "0 24px 60px rgba(15,23,42,0.14)",
  brand: `0 8px 22px ${colors.brand[500]}33`,
  brandHover: `0 12px 28px ${colors.brand[500]}40`,
  accent: `0 8px 22px ${colors.accent[500]}33`,
};

// Category visual mapping used by medicine/pharmacy cards
export const categoryPalette = {
  antibiotic: { label: "Antibiotic", bg: "#FFF4E6", fg: "#C2410C", dot: "#F97316" },
  painkiller: { label: "Pain Relief", bg: "#DBEAFE", fg: "#1D4ED8", dot: "#3B82F6" },
  fever: { label: "Fever & Pain", bg: "#FEE2E2", fg: "#B91C1C", dot: "#EF4444" },
  vitamin: { label: "Vitamins", bg: "#FEF3C7", fg: "#B45309", dot: "#F59E0B" },
  diabetic: { label: "Diabetic Care", bg: "#EDE9FE", fg: "#6D28D9", dot: "#8B5CF6" },
  heart: { label: "Heart Health", bg: "#FCE7F3", fg: "#BE185D", dot: "#EC4899" },
  digestive: { label: "Digestive", bg: "#DCFCE7", fg: "#15803D", dot: "#22C55E" },
  skin: { label: "Skincare", bg: "#FDF2F8", fg: "#9F1239", dot: "#F43F5E" },
  respiratory: { label: "Respiratory", bg: "#E0F2FE", fg: "#0369A1", dot: "#0EA5E9" },
  generic: { label: "General", bg: "#F1F5F9", fg: "#334155", dot: "#64748B" },
};

// Maps medicine attributes (category / composition / name keywords) to a palette key
export const inferCategory = (med) => {
  const haystack = [
    med?.category,
    med?.MedicinalUse,
    med?.medicinal_use,
    med?.Details,
    med?.details,
    med?.composition,
    med?.key_ingredients,
    med?.product_name,
    med?.Name,
    med?.name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (/antibiotic|amoxicillin|augmentin|azithromycin|ciprofloxacin|cephalexin/.test(haystack)) return "antibiotic";
  if (/paracetamol|crocin|dolo|ibuprofen|naproxen|diclofenac|pain/.test(haystack)) return "fever";
  if (/vitamin|supplement|calcium|iron|b12|d3|zinc/.test(haystack)) return "vitamin";
  if (/diabet|insulin|metformin|glipizide|sugar/.test(haystack)) return "diabetic";
  if (/heart|cardio|statin|atorva|amlodipine|losartan|bp/.test(haystack)) return "heart";
  if (/digest|antacid|omeprazole|pantoprazole|ranitidine|gas|acidity/.test(haystack)) return "digestive";
  if (/skin|cream|ointment|moisturizer|derma/.test(haystack)) return "skin";
  if (/cough|cold|asthma|respir|inhaler|montelukast|levolin|salbutamol/.test(haystack)) return "respiratory";
  if (/pain/.test(haystack)) return "painkiller";
  return "generic";
};

// Common Chakra component override styles
export const cardStyle = {
  bg: colors.neutral[0],
  border: `1px solid ${colors.neutral[200]}`,
  borderRadius: radii.lg,
  boxShadow: shadows.sm,
};

export const cardHoverStyle = {
  boxShadow: shadows.lg,
  transform: "translateY(-2px)",
  borderColor: colors.brand[200],
};

// CSS-in-JS helper for focus rings
export const focusRing = {
  _focusVisible: {
    boxShadow: `0 0 0 3px ${colors.brand[200]}`,
    outline: "none",
  },
};

// Named keyframes & animation helpers (used as inline <style>)
export const animations = `
@keyframes pillstack-fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
@keyframes pillstack-spring { 0% { transform: scale(1);} 35% { transform: scale(1.08);} 60% { transform: scale(.96);} 100% { transform: scale(1);} }
@keyframes pillstack-pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.06);opacity:.85} }
@keyframes pillstack-bounce { 0%,100%{transform:translateY(0)} 30%{transform:translateY(-4px)} 60%{transform:translateY(-2px)} }
@keyframes pillstack-shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
@keyframes pillstack-pin-drop { 0% { transform: translateY(-12px); opacity: 0;} 70% { transform: translateY(2px); } 100% { transform: translateY(0); opacity: 1;} }
@keyframes pillstack-progress-fill { from { transform: scaleX(0);} to { transform: scaleX(1);} }
`;

export const shimmerBg = `linear-gradient(90deg, ${colors.neutral[100]} 0%, ${colors.neutral[200]} 50%, ${colors.neutral[100]} 100%)`;

// Shared trust strip content
export const trustSignals = [
  { icon: "shield", label: "Licensed pharmacies" },
  { icon: "check", label: "AI-verified medicines" },
  { icon: "lock", label: "SSL secured checkout" },
  { icon: "truck", label: "Delivery in 45 mins" },
];

export default {
  colors,
  gradients,
  fontFamily,
  type,
  space,
  radii,
  shadows,
  categoryPalette,
  inferCategory,
  cardStyle,
  cardHoverStyle,
  focusRing,
  animations,
  shimmerBg,
  trustSignals,
};
